import { useData } from "../contexts/DataContext/DataContextt";
import { useCallback } from "react";
import PageContainer from "../components/Forms/PageContainer";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Button,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router";
import { deletValue, fetchValues } from "../services/valueService";
import { valuesPageCache } from "./cache/PageCache";
import { usePagedQuery } from "../contexts/hooks/usePagedQuery";
import TabulatedGridCollapsibleV2 from "../components/Forms/TabulatedGridCollapsibleV2";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { RowActions } from "../utils/RowActions";

const ValuePageNew = () => {
  const { language } = useData();
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleKeyDown,
    handleSearch,
    data: valuesData,
    total,
    searchTerm,
    isLoading,
    error,
    reload,
    handleDeleteAction,
    searchType,
    setSearchType,
    searchOption,
  } = usePagedQuery({
    fetchFunction: fetchValues,
    captureErrors: true,
    searchOption: [
      { id: "valueCode", display: "Code" },
      { id: "valueName", display: "Name" },
    ],
    language,
    cache: valuesPageCache,
  });

  const handleCreateClick = useCallback(() => {
    navigate(`/values/new`);
  }, [navigate]);

  const headers = [
    {
      prop: "valueCode",
      text: "Code",
      width: 160,
      link: {
        action: (row) =>
          navigate(`/value/edit/${row.valueCode}`, { state: { row } }),
      },
    },
    {
      prop: "valueCodeAlternative",
      text: "Alternative Code",
      width: 150,
      isImage: false,
    },
    { prop: "valueCodeOld", text: "Old Code", width: 150, isImage: false },
    { prop: "valueName", text: "Name", isImage: false },
    { prop: "valueDisplayName", text: "Display Name", isImage: false },
    { prop: "valueImage", text: "Symbols", width: 10, isImage: true },
  ];

  const pageRowActions = [
    {
      tooltip: "Edit Value",
      icon: <EditNoteRoundedIcon />,
      onClick: (row) =>
        navigate(`/value/edit/${row.valueCode}`, { state: { row } }),
    },
    {
      tooltip: "Delete Value",
      icon: <DeleteForeverRoundedIcon />,
      color: "error",
      onClick: (row) => {
        handleDeleteAction(
          async (value) => {
            const result = await deletValue(value);
            if (result instanceof Response && !result.ok) {
              throw new Error("Delete value failed");
            }
            return result;
          },
          row,
          "valueCode",
          {
            title: "Delete Value",
            message: `Are you sure you want to delete value ${row.valueCode}?`,
          },
        );
      },
    },
  ];

  return (
    <PageContainer
      title="ETIM Values"
      subtitle="Manage ETIM values"
      breadcrumbs={[{ title: "ETIM Values" }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Refresh values" placement="bottom" enterDelay={300}>
            <div>
              <IconButton
                size="small"
                aria-label="Refresh values"
                disabled={isLoading}
                onClick={() => {
                  reload(true);
                }}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box mt={3}>
        <TabulatedGridCollapsibleV2
          variant="modern"
          loading={isLoading}
          emptyState={
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 8, color: "text.secondary" }}
            >
              <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
              <Typography role={error ? "alert" : undefined}>
                {error ? "Unable to load values" : "No values found"}
              </Typography>
              {error ? (
                <Button size="small" onClick={() => reload(true)}>
                  Retry
                </Button>
              ) : searchTerm ? (
                <Button size="small" onClick={() => handleSearch("")}>
                  Clear search
                </Button>
              ) : null}
            </Stack>
          }
          pagination={{
            totalRows: total,
            page,
            rowsPerPage,
            handleChangePage,
            handleChangeRowsPerPage,
          }}
          search={{
            placeHolder: "Search values...",
            searchOption,
            searchType,
            setSearchType,
            handleSearchClick: handleSearch,
            onKeyDown: handleKeyDown,
            activeTerm: searchTerm,
            onClear: () => handleSearch(""),
          }}
          data={valuesData}
          headers={headers}
          rowActions={(row) => (
            <RowActions row={row} actions={pageRowActions} />
          )}
        />
      </Box>
    </PageContainer>
  );
};

export default ValuePageNew;
